import { can, firstName, type MeDto, studentQrPayload } from '@hudhuria/shared';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { z } from 'zod';
import { useTodayView } from '~/api/derived';
import { useCenter, useMe } from '~/api/queries';
import { CARD_H, CARD_W, IdCardBack, IdCardFront } from '~/components/id-card';
import {
  Avatar,
  EmptyState,
  FitWidth,
  Overlay,
  OverlayFooter,
  OverlayHeader,
  PrimaryButton,
  SecondaryButton,
  SegmentedControl,
  Toggle,
} from '~/components/ui';
import { useBack } from '~/lib/nav';
import { cardsPdf, openPdf, reservePdfTab } from '~/lib/pdf';
import { toast } from '~/lib/toast';

export const Route = createFileRoute('/_app/id-cards')({
  validateSearch: z.object({ student: z.string().optional() }),
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData<MeDto>(['me']);
    if (!can(me?.staff.role, 'idcards')) throw redirect({ to: '/hub' });
  },
  component: IdCards,
});

/** Student ID cards with a real QR (scan to check in, even offline). Print one on a card printer, or 10 per A4 sheet. */
function IdCards() {
  const { student: selectedId } = Route.useSearch();
  const navigate = useNavigate();
  const back = useBack('/me');
  const me = useMe().data;
  const center = useCenter();
  const view = useTodayView();
  const [side, setSide] = useState<'front' | 'back'>('front');
  const [showPhoto, setShowPhoto] = useState(true);
  const [printing, setPrinting] = useState<null | 'one' | 'all'>(null);
  const [batch, setBatch] = useState<{
    students: typeof students;
    layout: 'card' | 'a4';
    filename: string;
    tab: Window | null;
  } | null>(null);

  // Synced students only — a pending registration has no final ref to print yet.
  const students = useMemo(
    () =>
      view.centerRoster
        .filter((s) => s.homeCenterId === center?.id && !s.pending)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [view.centerRoster, center?.id],
  );
  const sel = students.find((s) => s.id === selectedId) ?? students[0];
  const label = me?.institution.idCardLabel || me?.institution.name || '';
  const slug = me?.institution.slug ?? '';
  const select = (id: string) => void navigate({ to: '.', search: { student: id }, replace: true });

  // keep the selected student visible in the strip
  useEffect(() => {
    if (sel)
      document.getElementById(`pick-${sel.id}`)?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [sel]);

  if (!me || !center) return <Overlay tone="soft">{null}</Overlay>;

  // Both print paths render the faces off-screen at full size, so whichever side is on screen doesn't matter.
  const printOne = () => {
    if (!sel) return;
    setPrinting('one');
    setBatch({ students: [sel], layout: 'card', filename: `ID-card-${sel.ref}.pdf`, tab: reservePdfTab() });
  };
  const printAll = () => {
    setPrinting('all');
    toast(`Preparing ${students.length} cards · 10 per A4 sheet`);
    setBatch({
      students,
      layout: 'a4',
      filename: `ID-cards-${center.name.replace(/\s+/g, '-')}.pdf`,
      tab: reservePdfTab(),
    });
  };

  return (
    <Overlay tone="soft">
      <OverlayHeader back="Back" onBack={back} title="ID card" subtitle="Standard CR80 size · 85.6 × 54 mm" />
      {students.length === 0 ? (
        <EmptyState>No students at {center.name} yet.</EmptyState>
      ) : (
        <>
          <div className="flex shrink-0 gap-[10px] px-5 pt-1 pb-3 scroll-x">
            {students.map((s) => {
              const on = s.id === sel?.id;
              return (
                <button
                  type="button"
                  id={`pick-${s.id}`}
                  key={s.id}
                  onClick={() => select(s.id)}
                  className="flex shrink-0 cursor-pointer flex-col items-center gap-1 border-none bg-transparent p-0"
                >
                  <Avatar
                    name={s.name}
                    photoUrl={s.photoUrl}
                    size={42}
                    radius="full"
                    fontSize={13}
                    style={{ boxShadow: on ? '0 0 0 3px #f5f7fb, 0 0 0 5px #0369a1' : 'none' }}
                  />
                  <div className="text-[10.5px] font-semibold" style={{ color: on ? '#0369a1' : '#64748b' }}>
                    {firstName(s.name)}
                  </div>
                </button>
              );
            })}
          </div>
          {sel && (
            <div className="flex flex-1 flex-col items-center gap-4 px-5 pt-1.5 pb-4 scroll-y">
              <div key={side} className="w-full animate-fade-in">
                <FitWidth width={CARD_W} height={CARD_H}>
                  {side === 'front' ? (
                    <IdCardFront
                      student={sel}
                      label={label}
                      centerName={center.name}
                      payload={studentQrPayload(slug, sel.ref)}
                      showPhoto={showPhoto}
                    />
                  ) : (
                    <IdCardBack student={sel} label={label} payload={studentQrPayload(slug, sel.ref)} />
                  )}
                </FitWidth>
              </div>
              <SegmentedControl
                className="w-full"
                tone="seg"
                value={side}
                onChange={setSide}
                options={[
                  { value: 'front', label: 'Front' },
                  { value: 'back', label: 'Back' },
                ]}
              />
              <div className="flex w-full items-center justify-between rounded-[14px] bg-white px-[14px] py-[13px]">
                <span className="text-[14px] font-medium">Show photo</span>
                <Toggle on={showPhoto} onChange={setShowPhoto} label="Show photo" />
              </div>
            </div>
          )}
        </>
      )}
      <OverlayFooter className="flex gap-[10px]">
        <SecondaryButton
          className="flex-1 px-[10px] text-[14px]"
          disabled={!students.length || !!printing}
          onClick={printAll}
        >
          {printing === 'all' ? 'Preparing…' : `Print all · ${students.length}`}
        </SecondaryButton>
        <PrimaryButton
          className="flex-[1.3] px-[10px] py-[15px] text-[14px]"
          disabled={!sel}
          loading={printing === 'one'}
          onClick={printOne}
        >
          Print {sel ? firstName(sel.name) : ''}'s card
        </PrimaryButton>
      </OverlayFooter>

      {batch && (
        <BatchRender
          {...batch}
          label={label}
          centerName={center.name}
          slug={slug}
          showPhoto={showPhoto}
          onDone={() => {
            setBatch(null);
            setPrinting(null);
          }}
        />
      )}
    </Overlay>
  );
}

/** Renders the cards off-screen, rasterises them into a PDF, opens it for printing, then unmounts. */
function BatchRender({
  students,
  layout,
  filename,
  tab,
  label,
  centerName,
  slug,
  showPhoto,
  onDone,
}: {
  students: {
    id: string;
    name: string;
    school: string;
    ref: string;
    photoUrl: string | null;
    guardianName: string;
    guardianPhone: string;
  }[];
  layout: 'card' | 'a4';
  filename: string;
  tab: Window | null;
  label: string;
  centerName: string;
  slug: string;
  showPhoto: boolean;
  onDone: () => void;
}) {
  const fronts = useRef<(HTMLDivElement | null)[]>([]);
  const backs = useRef<(HTMLDivElement | null)[]>([]);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        // let photos load before rasterising
        await new Promise((r) => setTimeout(r, 400));
        const faces = students.map((_, i) => ({ front: fronts.current[i]!, back: backs.current[i]! }));
        const pdf = await cardsPdf(faces, layout);
        await openPdf(filename, pdf, tab);
      } catch {
        tab?.close();
        toast("Couldn't prepare the cards — try again");
      } finally {
        onDone();
      }
    })();
  }, [students, layout, filename, tab, onDone]);

  return createPortal(
    <div aria-hidden className="pointer-events-none fixed top-0 -left-[10000px] flex flex-col gap-4">
      {students.map((s, i) => (
        <div key={s.id} className="flex gap-4">
          <IdCardFront
            ref={(el) => {
              fronts.current[i] = el;
            }}
            student={s}
            label={label}
            centerName={centerName}
            payload={studentQrPayload(slug, s.ref)}
            showPhoto={showPhoto}
            flat
          />
          <IdCardBack
            ref={(el) => {
              backs.current[i] = el;
            }}
            student={s}
            label={label}
            payload={studentQrPayload(slug, s.ref)}
            flat
          />
        </div>
      ))}
    </div>,
    document.body,
  );
}
